import ReactDOM from "react-dom/client";
import "src/index.css";
import App from "src/App";
import reportWebVitals from "src/reportWebVitals";
import "bootstrap/dist/css/bootstrap.min.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import "react-circular-progressbar/dist/styles.css";
import SignIn from "src/components/Authentication/SignIn";
import { ConfigurationProvider } from "src/Utils/Context/ConfigurationContext";
import { CookiesProvider } from "react-cookie";
import PageRestau from "src/components/Main/Pages/Home/PageRestau";
import { PasswordReset } from "src/components/HomePage/PasswordReset";
import "src/i18n";
import LandingPage from "src/components/LandingPage";
import StaffInvitationPage from "src/components/StaffInvitation/StaffInvitationPage";
import TermsAndConditions from "src/components/HomePage/Pages/TermsAndConditions";
import EmailVerificationPage from "src/components/HomePage/VerifyEmail";
import BookingsPage from "src/components/Main/Pages/Bookings";
import TableSchedulePage from "src/components/Main/Pages/TableSchedule";
import { Deals } from "src/components/Main/Pages/Deals";
import HomePage from "src/components/Main/Pages/Home/HomePage";
import RestaurantDashboard from "src/components/Main/Pages/RestaurantDashboard";
import UserProvider from "src/Utils/Context/UserContext";
import AdminControlPanel from "src/components/Main/Pages/AdminControlPanel";
import BulkCreateRestaurants from "src/components/Main/Pages/AdminControlPanel/BulkCreateRestaurants";
import BulkCreateMenus from "src/components/Main/Pages/AdminControlPanel/BulkCreateMenus";
import Organizations from "src/components/Main/Pages/AdminControlPanel/Organizations";
import Permissions from "src/components/Main/Pages/AdminControlPanel/Permissions";
import Capabilities from "src/components/Main/Pages/AdminControlPanel/Capabilities";
import Subscriptions from "src/components/Main/Pages/AdminControlPanel/Subscriptions";
import AdminPayments from "src/components/Main/Pages/AdminControlPanel/Payments";
import AccountRequests from "src/components/Main/Pages/AdminControlPanel/AccountRequests";
import DeleteStaff from "src/components/Main/Pages/AdminControlPanel/DeleteStaff";
import FeatureSwitches from "src/components/Main/Pages/AdminControlPanel/FeatureSwitches";
import MyOrganization from "src/components/Main/Pages/MyOrganization";
import StaffList from "src/components/Main/Pages/MyOrganization/Staff";
import StaffView from "src/components/Main/Pages/MyOrganization/Staff/StaffView";
import RolesList from "src/components/Main/Pages/MyOrganization/Roles";
import RoleView from "src/components/Main/Pages/MyOrganization/Roles/RoleView";
import KPIS from "src/components/Main/Pages/RestaurantAnalytics";
import Configuration from "src/components/Main/Pages/Configuration";
import Payments from "src/components/Main/Pages/Payments";
import { CapabilityBoundary } from "src/design/CapabilityBoundary";
import { BOOKING_FEATURES } from "src/business/bookingScope";
import ClientBookingPage from "src/components/Main/Pages/ClientBooking";
import { PublicPageLayout } from "src/components/PublicLayout/PublicPageLayout";
import PublicMenuPage from "src/components/Main/Pages/PublicMenu";
import MobileAccountDeletion from "./components/HomePage/Pages/MobileAccountDeletion";
import { Menu } from "src/components/Main/Pages/Menu";
import { OpeningHours } from "src/components/Main/Pages/Schedule/OpeningHours";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RestaurantGallery } from "./components/Main/Pages/Gallery/RestaurantGallery";
import { RestaurantAccessBoundary } from "src/design/RestaurantAccessBoundary";
import { NoRestaurant } from "src/components/Main/Pages/NoRestaurant/NoRestaurant";

import "./monitoring/sentry.config";

const root = ReactDOM.createRoot(document.getElementById("root"));

function Index() {
  return (
    <QueryClientProvider client={new QueryClient()}>
      <UserProvider>
        <ConfigurationProvider>
          <CookiesProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/" element={<App />}>
                  <Route index element={<LandingPage />} />
                  <Route
                    path="staff-invitation/:token"
                    element={<StaffInvitationPage />}
                  />
                  <Route path="terms" element={<TermsAndConditions />} />
                  <Route
                    path="MobileAccountDeletion"
                    element={<MobileAccountDeletion />}
                  />
                </Route>
                <Route element={<PublicPageLayout />}>
                  <Route
                    path="booking/:restaurant_city/:restaurant_name"
                    element={<ClientBookingPage />}
                  />
                  <Route
                    path="menu/:restaurant_city/:restaurant_name"
                    element={<PublicMenuPage />}
                  />
                </Route>
                <Route path="/login" element={<SignIn />} />
                <Route path="/restaurant" element={<PageRestau />}>
                  <Route element={<RestaurantAccessBoundary />}>
                    {/* The dashboard is the landing page, so it is not behind a
                        CapabilityBoundary: it gates each of its own sections
                        instead and always has something to show. */}
                    <Route
                      path=":restaurantId/dashboard"
                      element={<RestaurantDashboard />}
                    />
                    <Route
                      path=":restaurantId/info"
                      element={
                        <CapabilityBoundary feature="restaurant_management">
                          <HomePage />
                        </CapabilityBoundary>
                      }
                    />
                    <Route
                      path=":restaurantId"
                      element={<Navigate to="dashboard" replace />}
                    />
                    <Route
                      path=":restaurantId/menu"
                      element={
                        <CapabilityBoundary feature="menu_management">
                          <Menu />
                        </CapabilityBoundary>
                      }
                    />
                    <Route
                      path=":restaurantId/bookings"
                      element={
                        <CapabilityBoundary feature={BOOKING_FEATURES}>
                          <BookingsPage />
                        </CapabilityBoundary>
                      }
                    />
                    <Route
                      path=":restaurantId/table-schedule"
                      element={
                        <CapabilityBoundary feature="table_scheduling">
                          <TableSchedulePage />
                        </CapabilityBoundary>
                      }
                    />
                    <Route
                      path=":restaurantId/deals"
                      element={
                        <CapabilityBoundary feature="deals_management">
                          <Deals />
                        </CapabilityBoundary>
                      }
                    />
                    <Route
                      path=":restaurantId/schedule"
                      element={
                        <CapabilityBoundary feature="opening_hours_management">
                          <OpeningHours />
                        </CapabilityBoundary>
                      }
                    />
                    <Route
                      path=":restaurantId/gallery"
                      element={
                        <CapabilityBoundary feature="image_gallery_management">
                          <RestaurantGallery />
                        </CapabilityBoundary>
                      }
                    />
                    <Route
                      path=":restaurantId/analytics"
                      element={
                        <CapabilityBoundary feature="analytics_dashboard">
                          <KPIS />
                        </CapabilityBoundary>
                      }
                    />
                    <Route
                      path=":restaurantId/configuration"
                      element={<Configuration />}
                    />
                    {/* Deliberately not behind a CapabilityBoundary: the page
                        gates itself on organization ownership, and a restaurant
                        whose subscription has lapsed is exactly the one that
                        has to reach it. */}
                    <Route
                      path=":restaurantId/payments"
                      element={<Payments />}
                    />
                  </Route>
                  <Route path="my-organization" element={<MyOrganization />}>
                    <Route index element={<Navigate to="staff" replace />} />
                    <Route path="staff" element={<StaffList />} />
                    <Route path="staff/:staffId" element={<StaffView />} />
                    <Route path="roles" element={<RolesList />} />
                    <Route path="roles/:roleId" element={<RoleView />} />
                  </Route>
                  <Route path="no-restaurant" element={<NoRestaurant />} />
                  <Route
                    path="admin-control-panel"
                    element={<AdminControlPanel />}
                  >
                    <Route
                      path="account-requests"
                      element={<AccountRequests />}
                    />
                    <Route path="organizations" element={<Organizations />} />
                    <Route path="permissions" element={<Permissions />} />
                    <Route path="capabilities" element={<Capabilities />} />
                    <Route
                      path="feature-switches"
                      element={<FeatureSwitches />}
                    />
                    <Route path="subscriptions" element={<Subscriptions />} />
                    <Route path="payments" element={<AdminPayments />} />
                    <Route path="delete-staff" element={<DeleteStaff />} />
                    <Route
                      path="bulk-create-restaurants"
                      element={<BulkCreateRestaurants />}
                    />
                    <Route
                      path="bulk-create-menus"
                      element={<BulkCreateMenus />}
                    />
                  </Route>
                  <Route
                    path="*"
                    element={<Navigate to="/restaurant/no-restaurant" replace />}
                  />
                </Route>
                <Route
                  path="/reset-password/:token"
                  element={<PasswordReset />}
                />
                <Route
                  path="/verify-email/:token"
                  element={<EmailVerificationPage />}
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </BrowserRouter>
          </CookiesProvider>
        </ConfigurationProvider>
      </UserProvider>
    </QueryClientProvider>
  );
}

root.render(<Index />);

reportWebVitals();
